import type { EventAnalytics } from '@barso/shared';
import { useEffect, useState } from 'react';
import { analyticsApi } from '../api/endpoints';
import { messageFor } from '../api/messages';

/** So lange gelten geladene Auswertungen für alle Kacheln und Unterseiten (danach wird neu geladen). */
const MAX_AGE_MS = 60_000;

const cache = new Map<string, { at: number; promise: Promise<EventAnalytics> }>();

/** Leert den Zwischenspeicher (Tests, „Neu laden“). */
export function clearAnalyticsCache(eventId?: string): void {
  if (eventId === undefined) cache.clear();
  else cache.delete(eventId);
}

function load(eventId: string): Promise<EventAnalytics> {
  const hit = cache.get(eventId);
  if (hit !== undefined && Date.now() - hit.at < MAX_AGE_MS) return hit.promise;
  const promise = analyticsApi.event(eventId);
  cache.set(eventId, { at: Date.now(), promise });
  // Ein Fehler wird nicht gemerkt: Der nächste Versuch fragt den Server erneut
  promise.catch(() => {
    if (cache.get(eventId)?.promise === promise) cache.delete(eventId);
  });
  return promise;
}

/**
 * Lädt die Auswertungen eines Basars einmal und teilt sie mit allen Kacheln und Unterseiten (Plan 12). `enabled: false`
 * lädt nichts (z. B. ohne das Recht `event.reports.view`, der Server würde ohnehin ablehnen).
 */
export function useEventAnalytics(eventId: string, enabled = true) {
  const [data, setData] = useState<EventAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const guard = { cancelled: false };
    load(eventId).then(
      (loaded) => {
        if (guard.cancelled) return;
        setData(loaded);
        setError(null);
      },
      (caught: unknown) => {
        if (!guard.cancelled) setError(messageFor(caught));
      },
    );
    return () => {
      guard.cancelled = true;
    };
  }, [eventId, enabled, reloadKey]);

  return {
    data,
    error,
    loading: enabled && data === null && error === null,
    reload: () => {
      clearAnalyticsCache(eventId);
      setData(null);
      setError(null);
      setReloadKey((key) => key + 1);
    },
  };
}
